import React from 'react';
import { Column } from 'react-table';
import SessionTable, {
  ResponseGuard,
  SessionTableInstanceProps
} from '../index';
import {
  MappingCell,
  DirectCell,
  YesNoCell,
  GetFlags,
  CellFunction,
  RESPONSE_NA
} from '../../cells';

import { IEpxAsthma } from '../../../../../libs/apps/epxasthma';

const TriStateLiteral = {
  0: 'Worse',
  1: 'Same',
  2: 'Better'
};

const EmptyGuard = (value: any) => (value == null ? '?' : value);

type Dependencies = [boolean];

const getDependencies = (sessions: IEpxAsthma[]): Dependencies => {
  let shouldAskFever = false;
  for (const session of sessions) {
    if (session.hasFever != null) {
      shouldAskFever = true;
    }
  }
  return [shouldAskFever];
};

const ScreeningCell: CellFunction<IEpxAsthma> = ({
  row: { original: session }
}) => {
  if (!(session as IEpxAsthma).isScreening) {
    return RESPONSE_NA;
  }
  return GetFlags(session as IEpxAsthma, [
    (session) => {
      return [`Weekly RI Uses: ${EmptyGuard(session.weeklyRescueUses)}`];
    },
    (session) => {
      return [`Single Day RI Uses: ${EmptyGuard(session.rescueUsesInDay)}`];
    },
    (session) => {
      return [`Times Awakened: ${EmptyGuard(session.timesAwakened)}`];
    }
  ]);
};

const getColumns = (sessions: IEpxAsthma[]): Column<IEpxAsthma>[] => {
  const [shouldAskFever] = getDependencies(sessions);
  const columns: Column<IEpxAsthma>[] = [
    {
      Header: 'Breathing',
      id: 'breathing',
      Cell: ResponseGuard(
        MappingCell('breathing', TriStateLiteral) as CellFunction<IEpxAsthma>
      )
    },
    {
      Header: 'Rescue Inhaler Uses',
      id: 'rescueInhalerUses',
      Cell: ResponseGuard(
        DirectCell('rescueInhalerUses') as CellFunction<IEpxAsthma>
      )
    },
    {
      Header: 'Symptoms Improved',
      id: 'symptomsImproved',
      Cell: ResponseGuard(
        YesNoCell('symptomsImproved') as CellFunction<IEpxAsthma>
      )
    },
    {
      Header: 'Screening',
      id: 'screening',
      Cell: ScreeningCell
    }
  ];
  if (shouldAskFever) {
    columns.push({
      Header: 'Has Fever',
      id: 'hasFever',
      Cell: ResponseGuard(YesNoCell('hasFever') as CellFunction<IEpxAsthma>)
    });
  }
  return columns;
};

const EpxAsthmaTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  return (
    <SessionTable<IEpxAsthma>
      url="/epxasthma/data/"
      getColumns={getColumns}
      getDependencies={getDependencies}
      {...props}
    />
  );
};

export default EpxAsthmaTable;
